'use client';

import { useState, useTransition } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { useToast } from '@/shared/components/ui/use-toast';
import { storyKeys } from '@/contexts/stories/queryKeys';
import { requestPublication, withdrawPublicationRequest } from '../actions/publicationInvite';

type OwnerStatus = 'none' | 'nominated' | 'pending' | 'approved' | 'publishing' | 'published' | 'publish_failed' | 'rejected';

/**
 * Gallery publication, as ONE sidebar row.
 *
 * It used to be a titled Card with a paragraph of explanation, which made it
 * the biggest and oddest-looking thing in a sidebar whose every other action
 * ("Download PDF", "Export video") is a single row. The explanation belongs in
 * the confirmation dialog — the row only offers the action and reports where
 * the request stands.
 *
 * Rejections are reported as "in review" on purpose (public-publishing-plan.md:
 * authors are not told about rejections).
 */
export function PublicationRequestCard({
  storyId,
  publicStatus,
  textReady,
}: {
  storyId: string;
  publicStatus: OwnerStatus;
  textReady: boolean;
}) {
  const t = useTranslations('stories.workspace.publicationRequest');
  const tConsent = useTranslations('content.wizard.output');
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pending, startTransition] = useTransition();
  const [confirm, setConfirm] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });

  const submit = () =>
    startTransition(async () => {
      const res = await requestPublication(storyId);
      setConfirm(false);
      if (!res.success) {
        toast({ variant: 'destructive', description: res.error });
        return;
      }
      toast({ description: t('sent') });
      refresh();
    });

  const withdraw = () =>
    startTransition(async () => {
      const res = await withdrawPublicationRequest(storyId);
      if (!res.success) toast({ variant: 'destructive', description: res.error });
      refresh();
    });

  // Admin nomination is handled by the invite banner in the main column.
  if (publicStatus === 'nominated' || !textReady) return null;

  const state: 'idle' | 'review' | 'approved' | 'published' =
    publicStatus === 'published'
      ? 'published'
      : publicStatus === 'approved' || publicStatus === 'publishing' || publicStatus === 'publish_failed'
        ? 'approved'
        : publicStatus === 'pending' || publicStatus === 'rejected'
          ? 'review'
          : 'idle';

  const statusLabel =
    state === 'review' ? t('statusReview') : state === 'approved' ? t('statusApproved') : t('statusPublished');

  return (
    <div className="space-y-2" data-testid="publication-request" data-state={state}>
      {state === 'idle' && (
        <Button
          variant="outline"
          size="sm"
          className="justify-start w-full"
          disabled={pending}
          onClick={() => setConfirm(true)}
          data-testid="publication-request-open"
        >
          <Globe className="mr-1.5 size-4 shrink-0" />
          <span className="truncate">{t('propose')}</span>
        </Button>
      )}

      {state === 'published' ? (
        <Button
          variant="outline"
          size="sm"
          className="justify-start w-full"
          asChild
          data-testid="publication-request-published"
        >
          <a href={`/${locale}/gallery/${storyId}`} target="_blank" rel="noreferrer">
            <Globe className="mr-1.5 size-4 shrink-0" />
            <span className="truncate">{t('rowTitle')}</span>
            <span className="ml-auto pl-2 text-xs text-muted-foreground">{statusLabel} ↗</span>
          </a>
        </Button>
      ) : state !== 'idle' ? (
        <div
          className="flex w-full items-center rounded-md border px-3 py-2 text-sm"
          data-testid={state === 'review' ? 'publication-request-review' : 'publication-request-approved'}
        >
          <Globe className="mr-1.5 size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{t('rowTitle')}</span>
          <span className="ml-auto pl-2 text-xs text-muted-foreground">{statusLabel}</span>
        </div>
      ) : null}

      {publicStatus === 'pending' && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full justify-start px-3 text-xs text-muted-foreground"
          disabled={pending}
          onClick={withdraw}
          data-testid="publication-request-withdraw"
        >
          {t('withdraw')}
        </Button>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              <span className="block">{t('idle')}</span>
              <span className="block">{tConsent('publicConsentDetails')}</span>
              <span className="block text-xs">{t('confirmNote')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={pending} onClick={(e) => { e.preventDefault(); submit(); }} data-testid="publication-request-confirm">
              {t('confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
